<script setup lang="ts">
import {computed, ref} from "vue";
import dayjs from "dayjs";
import {useRouter} from "vue-router";
import useSwr from "@/core/composables/swr/useSwr";
import {teamApi} from "@/container";
import LegacyAccess from "@/core/interfaces/Access";
import {TeamTask} from "@/core/interfaces/TeamTask";
import useTeamTasks from "@/team-task/composables/useTeamTasks";
import {initialsOf, priorityOption} from "@/team-task/taskBoard";
import {ListChecks, UserRoundX} from "@lucide/vue";

type DayFilter = 'all' | 'mine' | 'unassigned' | 'done';

const {currentAccessId} = defineProps<{
    /** Access point of the session, for the « À moi » chip. */
    currentAccessId: number | null;
}>();

const router = useRouter();

/**
 * The very cache the full screen reads: the two surfaces cannot drift, and a
 * change made on one shows on the other without a refetch.
 */
const {tasks} = useTeamTasks();

const {data: teamData} = useSwr('access/current/team', () => teamApi().index());
const members = computed<LegacyAccess[]>(() => teamData.value?.team ?? []);

const filter = ref<DayFilter>('all');

/**
 * What the team is on today: anything due today, plus anything already under
 * way whatever its due date.
 *
 * Waiting for the due date would hide work in progress until the very day it
 * falls due — the rail would stay empty while the team is busy. A task that is
 * neither started nor due today stays on the board only.
 */
const ofTheDay = computed(() => {
    const today = dayjs().format('YYYY-MM-DD');

    return tasks.value.filter(task => task.due_date === today || task.status === 'doing');
});

const matches = (task: TeamTask, key: DayFilter): boolean => {
    switch (key) {
        case 'mine':
            return currentAccessId !== null && task.assignee_ids.includes(currentAccessId);
        case 'unassigned':
            return !task.assignee_ids.length;
        case 'done':
            return task.status === 'done';
        default:
            return true;
    }
};

const filters: { key: DayFilter; label: string }[] = [
    {key: 'all', label: 'Toutes'},
    {key: 'mine', label: 'À moi'},
    {key: 'unassigned', label: 'Non assignées'},
    {key: 'done', label: 'Terminées'}
];

const visible = computed(() => ofTheDay.value.filter(task => matches(task, filter.value)));

const avatarsOf = (task: TeamTask) =>
    task.assignee_ids
        .map(id => members.value.find(member => member.id === id))
        .filter((member): member is LegacyAccess => !!member)
        .slice(0, 3);

/** The full screen owns the detail modal; the rail just points at the task. */
const open = (task: TeamTask) => router.push({path: '/team-tasks', query: {task: task.id}});
</script>

<template>
  <section class="rounded-lg border border-gray-200 bg-white p-3">
    <header class="mb-2 flex items-center gap-1.5">
      <ListChecks class="size-4 text-primary" />
      <h2 class="text-xs font-semibold text-gray-800">
        Tâches du jour
      </h2>
      <span class="ml-auto rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-bold text-gray-500">
        {{ ofTheDay.length }}
      </span>
    </header>

    <div class="mb-2 flex flex-wrap gap-1">
      <button
        v-for="chip in filters"
        :key="chip.key"
        class="rounded-full border px-2 py-0.5 text-[10px] font-medium transition"
        :class="filter === chip.key
          ? 'border-primary bg-primary/5 text-primary'
          : 'border-gray-200 text-gray-600 hover:border-gray-300'"
        @click="filter = chip.key"
      >
        {{ chip.label }}
      </button>
    </div>

    <p v-if="!visible.length" class="py-3 text-center text-[11px] text-gray-400">
      Rien en cours ni prévu aujourd'hui.
    </p>

    <ul v-else class="space-y-0.5">
      <li v-for="task in visible" :key="task.id">
        <button
          class="flex w-full items-center gap-2 rounded-lg px-1.5 py-1.5 text-left transition hover:bg-gray-50"
          @click="open(task)"
        >
          <span class="size-1.5 shrink-0 rounded-full" :class="priorityOption(task.priority).dotClass" />
          <span
            class="min-w-0 flex-1 truncate text-[11px]"
            :class="task.status === 'done' ? 'text-gray-400 line-through' : 'text-gray-700'"
          >
            {{ task.title }}
          </span>
          <UserRoundX
            v-if="!task.assignee_ids.length"
            class="size-3.5 shrink-0 text-amber-500"
            aria-label="Non assignée"
          />
          <span v-else class="flex -space-x-1">
            <span
              v-for="member in avatarsOf(task)"
              :key="member.id"
              class="flex size-4 items-center justify-center rounded-full border border-white bg-primary/10 text-[8px] font-bold text-primary"
              :title="member.user?.name ?? ''"
            >
              {{ initialsOf(member.user?.name) }}
            </span>
          </span>
        </button>
      </li>
    </ul>

    <button
      class="mt-2 w-full rounded-lg border border-gray-200 py-1.5 text-[11px] font-semibold text-primary transition hover:border-primary/40"
      @click="router.push('/team-tasks')"
    >
      Toutes les tâches
    </button>
  </section>
</template>
